import {AxiosError} from 'axios';
import {Form, FormikProps, FormikProvider, useFormik} from 'formik';
import {forEach, isBoolean, isEmpty, isNumber, map} from 'lodash';
import {useCallback, useEffect, useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';
import * as Yup from 'yup';

import {settingsApi} from 'api';
import {
    Accordion,
    AccordionDetails,
    AccordionTitle,
    AssignmentSelectorsFormik, Button, Card, DataWrapper, MultiInputFormik,
    PredefinedNotifierType, Radio, UnsavedChangesLeavePrompt
} from 'components';
import {useApiData, useUserInfoSchema} from 'hooks';
import {CustomField, CustomFieldType, Group} from 'interfaces';
import {TRANSLATION_KEYS} from 'locale';
import {InviteeParticipantRole} from 'types';
import {apiUtils, buildTranslationKey, dataUtils} from 'utils';

import {CustomFieldEditor} from './CustomFieldEditor';
import {FORM_FIELD_KEYS} from './constants';

const T_KEYS = TRANSLATION_KEYS.USER.ACCOUNT.SESSION.IN_SESSION.USER_INFO_SCHEMA_BUILDER;
const T_KEYS_COMMON = TRANSLATION_KEYS.COMMON;

type Values = {
    assignment: {
        groupId: string;
    };
    userInfoFields: CustomField[];
    controlOnlyInfoFields: CustomField[];
}

type FormTemplateProps = {
    groups: Group[];
    error: AxiosError<any>;
} & FormikProps<Values>

const FormTemplate: React.FC<FormTemplateProps> = ({
    values, error, initialValues, groups, isValid, dirty, setFieldValue, setFieldError
}) => {
    const {t} = useTranslation();

    const hasNoChanges = useMemo(() => dataUtils.isEqualValues(values, initialValues, true), [initialValues, values]);

    const validateItem = useCallback((item: CustomField) => !!item?.name, []);

    const getSectionName = useCallback((section: keyof Values) => {
        const key = buildTranslationKey(section);
        return t(T_KEYS.SECTION[key]);
    }, [t]);

    const handleAttributeAdd = useCallback((path: string, value: any) => {
        setFieldValue(path, value);
    }, [setFieldValue]);

    const handleAttributeRemove = useCallback((path: string) => {
        // Will delete the field
        setFieldValue(path, undefined);
    }, [setFieldValue]);

    const fieldsToValidate = useMemo(() => {
        const result = [];
        forEach(values.userInfoFields, (_, index) => {
            forEach(FORM_FIELD_KEYS, (fieldName) => {
                result.push({field: `userInfoFields.${index}.${fieldName}`});
            });
        });

        forEach(values.controlOnlyInfoFields, (field, index) => {
            forEach(FORM_FIELD_KEYS, (fieldName) => {
                result.push({field: `controlOnlyInfoFields.${index}.${fieldName}`});
            });
        });

        return result;
    }, [values]);

    const renderFieldsSection = useCallback((section: keyof Values) => (
        <Accordion shadow={false} padding={false} className='tw-py-4'>
            <AccordionTitle>{getSectionName(section)}</AccordionTitle>
            <AccordionDetails>
                <MultiInputFormik
                    name={section}
                    itemsPath={section}
                    defaultValue={{name: '', type: CustomFieldType.String, title: ''} as CustomField}
                    values={values}
                    isValid={validateItem}
                >
                    {({index, item, last, path, renderActions}) => (
                        <div
                            key={index}
                            className='tw-group tw-my-3 tw-border tw-border-default tw-rounded-sm tw-overflow-hidden tw-max-w-screen-sm'
                        >
                            <CustomFieldEditor
                                item={item}
                                path={path}
                                last={last}
                                renderDeleteAction={item?.name !== 'id' ? () => renderActions({index, item}) : undefined}
                                onAdd={handleAttributeAdd}
                                onRemove={handleAttributeRemove}
                            />
                        </div>
                    )}
                </MultiInputFormik>
            </AccordionDetails>
        </Accordion>
    ), [getSectionName, validateItem, values, handleAttributeAdd, handleAttributeRemove]);

    useEffect(() => {
        const errors = apiUtils.getFieldsValidationErrorMessages(error, t, fieldsToValidate);
        forEach(errors, (value, key) => value && setFieldError(key, value));
    }, [error, t, values, setFieldError, fieldsToValidate]);

    return (
        <Form className='tw-my-4'>
            <AssignmentSelectorsFormik name='assignment' groups={groups}>
                {({renderGroup}) => (
                    <div className='tw-mr-12 tw-grid md:tw-grid-cols-2 lg:tw-grid-cols-3'>
                        {renderGroup()}
                    </div>
                )}
            </AssignmentSelectorsFormik>
            {renderFieldsSection('userInfoFields')}
            {renderFieldsSection('controlOnlyInfoFields')}
            <Button
                color='primary'
                className='tw-mt-4'
                disabled={!isValid && dirty || !dirty || hasNoChanges}
                type='submit'
            >
                {t(T_KEYS_COMMON.BUTTONS.SAVE)}
            </Button>
            <UnsavedChangesLeavePrompt values={values} initialValues={initialValues} />
        </Form>
    );
};

type Props = {
    groups: Group[];
    guestEnabled?: boolean;
}

export const UserInfoSchemaBuilder: React.FC<Props> = ({groups, guestEnabled}) => {
    const {t} = useTranslation();
    const [selectedInvitee, setSelectedInvitee] = useState<InviteeParticipantRole>(InviteeParticipantRole.Client);
    const {
        getUserInfoFields, getControlOnlyFields, fetchUserInfoSchema, loading: loadingFetch
    } = useUserInfoSchema({fetchOnInit: false, role: selectedInvitee, guestEnabled});

    const {
        send: saveClient, loading: loadingClientUpdate, error: clientError, reset: clientReset
    } = useApiData(settingsApi.updateUserInfoSchema, {
        notificationType: PredefinedNotifierType.Save,
        onSuccess: () => fetchUserInfoSchema()
    });

    const {
        send: saveGuest, loading: loadingGuestUpdate, error: guestError, reset: guestReset
    } = useApiData(settingsApi.updateGuestUserInfoSchema, {
        notificationType: PredefinedNotifierType.Save,
        onSuccess: () => fetchUserInfoSchema()
    });

    const save = useMemo(() =>
        selectedInvitee === InviteeParticipantRole.Client ? saveClient : saveGuest, [selectedInvitee, saveClient, saveGuest]);
    const loadingUpdate = useMemo(() =>
        selectedInvitee === InviteeParticipantRole.Client ? loadingClientUpdate : loadingGuestUpdate,
    [selectedInvitee, loadingClientUpdate, loadingGuestUpdate]);
    const error = useMemo(() =>
        selectedInvitee === InviteeParticipantRole.Client ? clientError : guestError, [selectedInvitee, clientError, guestError]);
    const reset = useMemo(() =>
        selectedInvitee === InviteeParticipantRole.Client ? clientReset : guestReset, [selectedInvitee, clientReset, guestReset]);

    const [initialValues, setInitialValues] = useState<Values>({
        assignment: {
            groupId: '',
        },
        userInfoFields: [],
        controlOnlyInfoFields: [],
    });

    const validationSchema = useMemo(() => {
        const itemSchema = Yup.object().shape({
            name: Yup.string().required(),
            type: Yup.string().required(),
            title: Yup.string(),
            minLength: Yup.number(),
            maxLength: Yup.number(),
            minimum: Yup.number(),
            maximum: Yup.number(),
            enum: Yup.array()
        });

        return Yup.object().shape({
            assignment: Yup.object().shape({
                groupId: Yup.string()
            }),
            userInfoFields: Yup.array().of(itemSchema),
            controlOnlyInfoFields: Yup.array().of(itemSchema)
        });
    }, []);

    const formatCustomField = useCallback((field: CustomField): CustomField => ({
        name: field.name,
        type: field.type,
        ...(field.title && {title: field.title}),
        ...(field.format && {format: field.format}),
        ...(field.pattern && {pattern: field.pattern}),
        ...(isBoolean(field.required) && {required: field.required}),
        ...(isBoolean(field.nullable) && {nullable: field.nullable}),
        ...(isNumber(field.minLength) && {minLength: field.minLength}),
        ...(isNumber(field.maxLength) && {maxLength: field.maxLength}),
        ...(isNumber(field.minimum) && {minimum: field.minimum}),
        ...(isNumber(field.maximum) && {maximum: field.maximum}),
        ...(!isEmpty(field.enum) && {enum: field.enum}),
    }), []);

    const handleSubmit = useCallback((values: Values) => {
        save({
            assignment: values.assignment,
            userInfoFields: map(values.userInfoFields, formatCustomField),
            controlOnlyInfoFields: map(values.controlOnlyInfoFields, formatCustomField)
        });
    }, [formatCustomField, save]);

    const formik = useFormik({
        enableReinitialize: true,
        initialValues,
        validationSchema,
        onSubmit: handleSubmit,
    });
    const {values: formValues} = formik;

    useEffect(() => {
        fetchUserInfoSchema();
    }, [fetchUserInfoSchema]);

    useEffect(() => {
        const userInfoFields = getUserInfoFields(formValues.assignment);
        const controlOnlyInfoFields = getControlOnlyFields(formValues.assignment);

        setInitialValues({assignment: formValues.assignment, userInfoFields, controlOnlyInfoFields});
    }, [formValues.assignment, getUserInfoFields, getControlOnlyFields]);

    useEffect(() => {
        reset();
    }, [formValues.assignment, reset]);

    return (
        <div className='tw-relative tw-my-10'>
            <Card className='tw-px-10 -sm:tw-px-5'>
                <div className='tw-ts-h5-bold'>{t(T_KEYS.TITLE)}</div>
                {guestEnabled && (
                    <div className='tw-mt-4'>
                        <Radio
                            id='selected-invitee-client'
                            name='selected-invitee'
                            labelPlacement='left'
                            className='tw-mb-4 sm:tw-mr-4'
                            label={t(T_KEYS_COMMON.SESSION.LABELS.CLIENT)}
                            checked={selectedInvitee === InviteeParticipantRole.Client}
                            value={InviteeParticipantRole.Client}
                            onSelect={setSelectedInvitee}
                        />
                        <Radio
                            id='selected-invitee-guest'
                            name='selected-invitee'
                            labelPlacement='left'
                            className='tw-mb-4 sm:tw-mr-4'
                            label={t(T_KEYS_COMMON.SESSION.LABELS.GUEST)}
                            checked={selectedInvitee === InviteeParticipantRole.Guest}
                            value={InviteeParticipantRole.Guest}
                            onSelect={setSelectedInvitee}
                        />
                    </div>
                )}
                <DataWrapper isLoading={loadingFetch || loadingUpdate} hideContentOnLoading={false}>
                    <FormikProvider value={formik}>
                        <FormTemplate {...formik} groups={groups} error={error} />
                    </FormikProvider>
                </DataWrapper>
            </Card>
        </div>
    );
};